<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';

const store = useStore();
const { t } = useI18n();

const from = ref(0);
const to = ref(0);
const selectedPortal = ref(null);
const selectedStatus = ref(null);
const pageIndex = ref(0);
const isLoading = ref(false);
const isLoadingTimeline = ref(false);

const articles = ref([]);
const timelineData = ref([]);
const summary = ref({
  total_views: 0,
  total_articles: 0,
  average_views: 0,
  top_article: null,
});
const meta = ref({
  current_page: 1,
  total_pages: 1,
  total_count: 0,
});

const requestPayload = computed(() => ({
  since: from.value,
  until: to.value,
  portal_id: selectedPortal.value?.slug,
  status: selectedStatus.value?.value,
  page: pageIndex.value + 1,
  per_page: 25,
}));

const fetchSummary = async () => {
  try {
    const params = new URLSearchParams();
    if (from.value) params.append('since', from.value);
    if (to.value) params.append('until', to.value);
    if (selectedPortal.value?.slug)
      params.append('portal_id', selectedPortal.value.slug);
    if (selectedStatus.value?.value)
      params.append('status', selectedStatus.value.value);

    const response = await store.dispatch(
      'getArticleReportsSummary',
      params.toString()
    );
    summary.value = response.data;
  } catch (error) {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  }
};

const fetchArticles = async () => {
  try {
    isLoading.value = true;
    const params = new URLSearchParams();
    if (from.value) params.append('since', from.value);
    if (to.value) params.append('until', to.value);
    if (selectedPortal.value?.slug)
      params.append('portal_id', selectedPortal.value.slug);
    if (selectedStatus.value?.value)
      params.append('status', selectedStatus.value.value);
    params.append('page', pageIndex.value + 1);
    params.append('per_page', 25);

    const response = await store.dispatch(
      'getArticleReports',
      params.toString()
    );
    articles.value = response.data.articles;
    meta.value = response.data.meta;
  } catch (error) {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const fetchTimeline = async () => {
  try {
    isLoadingTimeline.value = true;
    const params = new URLSearchParams();
    if (from.value) params.append('since', from.value);
    if (to.value) params.append('until', to.value);
    if (selectedPortal.value?.slug)
      params.append('portal_id', selectedPortal.value.slug);
    if (selectedStatus.value?.value)
      params.append('status', selectedStatus.value.value);
    params.append('group_by', 'day');

    const response = await store.dispatch(
      'getArticleReportsTimeline',
      params.toString()
    );
    timelineData.value = response.data.data;
  } catch (error) {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    isLoadingTimeline.value = false;
  }
};

const fetchAllData = () => {
  fetchSummary();
  fetchArticles();
  fetchTimeline();
};

const onFilterChange = ({ from: fromValue, to: toValue }) => {
  from.value = fromValue;
  to.value = toValue;
  pageIndex.value = 0;
  fetchAllData();
};

const onPortalChange = portal => {
  selectedPortal.value = portal;
  pageIndex.value = 0;
  fetchAllData();
};

const onStatusChange = status => {
  selectedStatus.value = status;
  pageIndex.value = 0;
  fetchAllData();
};

const onPageChange = newPage => {
  pageIndex.value = newPage;
  fetchArticles();
};

const formatDate = dateString => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const downloadReports = () => {
  const csvContent = generateCSV();
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `article_views_report_${Date.now()}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const generateCSV = () => {
  const headers = ['Title', 'Category', 'Views', 'Last Viewed', 'URL'];
  const rows = articles.value.map(article => [
    `"${article.title.replace(/"/g, '""')}"`,
    `"${article.category_name || 'N/A'}"`,
    article.views,
    article.last_viewed_at ? formatDate(article.last_viewed_at) : 'N/A',
    `"hc/${article.portal_slug}/articles/${article.slug}"`,
  ]);
  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
};

const chartData = computed(() => {
  if (!timelineData.value || timelineData.value.length === 0) {
    return {
      labels: [],
      datasets: [],
    };
  }

  const labels = timelineData.value.map(item => {
    return format(fromUnixTime(item.timestamp), 'dd-MMM');
  });

  const data = timelineData.value.map(item => item.count);

  return {
    labels,
    datasets: [
      {
        label: t('ARTICLE_REPORTS.VIEWS'),
        data,
        backgroundColor: 'rgba(31, 147, 255, 0.2)',
        borderColor: 'rgba(31, 147, 255, 1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
      },
    ],
  };
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      mode: 'index',
      intersect: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0,
      },
    },
  },
}));
</script>

<template>
  <ReportHeader :header-title="$t('ARTICLE_REPORTS.HEADER')">
    <V4Button
      :label="$t('ARTICLE_REPORTS.DOWNLOAD')"
      icon="i-ph-download-simple"
      size="sm"
      @click="downloadReports"
    />
  </ReportHeader>

  <div class="flex flex-col gap-4">
    <ReportFilterSelector
      :show-agents-filter="false"
      :show-inbox-filter="false"
      :show-team-filter="false"
      :show-group-by-filter="false"
      :show-business-hours-switch="false"
      @filter-change="onFilterChange"
    />

    <!-- Summary Metrics -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <ReportMetricCard
        :header="$t('ARTICLE_REPORTS.TOTAL_VIEWS')"
        :loading="isLoading"
      >
        <div class="text-3xl font-semibold text-n-slate-12">
          {{ summary.total_views.toLocaleString() }}
        </div>
      </ReportMetricCard>

      <ReportMetricCard
        :header="$t('ARTICLE_REPORTS.TOTAL_ARTICLES')"
        :loading="isLoading"
      >
        <div class="text-3xl font-semibold text-n-slate-12">
          {{ summary.total_articles.toLocaleString() }}
        </div>
      </ReportMetricCard>

      <ReportMetricCard
        :header="$t('ARTICLE_REPORTS.AVERAGE_VIEWS')"
        :loading="isLoading"
      >
        <div class="text-3xl font-semibold text-n-slate-12">
          {{ summary.average_views.toLocaleString() }}
        </div>
      </ReportMetricCard>

      <ReportMetricCard
        :header="$t('ARTICLE_REPORTS.TOP_ARTICLE')"
        :loading="isLoading"
      >
        <div v-if="summary.top_article" class="text-sm">
          <div class="font-semibold text-n-slate-12 truncate">
            {{ summary.top_article.title }}
          </div>
          <div class="text-n-slate-11">
            {{ summary.top_article.views.toLocaleString() }}
            {{ $t('ARTICLE_REPORTS.VIEWS') }}
          </div>
        </div>
        <div v-else class="text-sm text-n-slate-11">
          {{ $t('ARTICLE_REPORTS.NO_DATA') }}
        </div>
      </ReportMetricCard>
    </div>

    <!-- Timeline Chart -->
    <div class="bg-n-solid-2 rounded-lg border border-n-weak p-6">
      <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
        {{ $t('ARTICLE_REPORTS.VIEWS_OVER_TIME') }}
      </h3>
      <div
        v-if="isLoadingTimeline"
        class="h-64 flex items-center justify-center"
      >
        <span class="text-n-slate-11">{{ $t('ARTICLE_REPORTS.LOADING') }}</span>
      </div>
      <div
        v-else-if="timelineData.length === 0"
        class="h-64 flex items-center justify-center"
      >
        <span class="text-n-slate-11">{{ $t('ARTICLE_REPORTS.NO_DATA') }}</span>
      </div>
      <div v-else class="h-64">
        <BarChart :collection="chartData" :chart-options="chartOptions" />
      </div>
    </div>

    <!-- Articles Table -->
    <div class="bg-n-solid-2 rounded-lg border border-n-weak overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-n-slate-2 border-b border-n-weak">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('ARTICLE_REPORTS.TABLE.TITLE') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('ARTICLE_REPORTS.TABLE.CATEGORY') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('ARTICLE_REPORTS.TABLE.VIEWS') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('ARTICLE_REPORTS.TABLE.LAST_VIEWED') }}
              </th>
            </tr>
          </thead>
          <tbody class="bg-n-solid-2 divide-y divide-n-weak">
            <tr v-if="isLoading">
              <td colspan="4" class="px-6 py-4 text-center text-n-slate-11">
                {{ $t('ARTICLE_REPORTS.LOADING') }}
              </td>
            </tr>
            <tr v-else-if="articles.length === 0">
              <td colspan="4" class="px-6 py-4 text-center text-n-slate-11">
                {{ $t('ARTICLE_REPORTS.NO_ARTICLES') }}
              </td>
            </tr>
            <tr
              v-for="article in articles"
              v-else
              :key="article.id"
              class="hover:bg-n-slate-2 transition-colors"
            >
              <td class="px-6 py-4 text-sm text-n-slate-12">
                <div class="font-medium">{{ article.title }}</div>
              </td>
              <td class="px-6 py-4 text-sm text-n-slate-11">
                {{ article.category_name || $t('ARTICLE_REPORTS.NO_CATEGORY') }}
              </td>
              <td class="px-6 py-4 text-sm text-n-slate-12 font-semibold">
                {{ article.views.toLocaleString() }}
              </td>
              <td class="px-6 py-4 text-sm text-n-slate-11">
                {{
                  article.last_viewed_at
                    ? formatDate(article.last_viewed_at)
                    : $t('ARTICLE_REPORTS.NO_DATA')
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div
        v-if="meta.total_pages > 1"
        class="px-6 py-4 flex items-center justify-between border-t border-n-weak"
      >
        <div class="text-sm text-n-slate-11">
          {{
            $t('ARTICLE_REPORTS.SHOWING_RESULTS', {
              from: (meta.current_page - 1) * 25 + 1,
              to: Math.min(meta.current_page * 25, meta.total_count),
              total: meta.total_count,
            })
          }}
        </div>
        <div class="flex gap-2">
          <V4Button
            :disabled="meta.current_page === 1"
            :label="$t('ARTICLE_REPORTS.PREVIOUS')"
            size="sm"
            variant="secondary"
            @click="onPageChange(pageIndex - 1)"
          />
          <V4Button
            :disabled="meta.current_page === meta.total_pages"
            :label="$t('ARTICLE_REPORTS.NEXT')"
            size="sm"
            variant="secondary"
            @click="onPageChange(pageIndex + 1)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
